import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { MetricAnomalies, RecordData, Service } from 'webapp-types';
import { BillingFeature, ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { MetricSetup } from 'webapp/models-custom';
import MetricAnomaly from 'webapp/models-custom/metric-anomaly';
import type MetricAnomalyRecord from 'webapp/models/metric-anomaly';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

type RequestVariablePrefix = 'metricAnomalies';

export default class ModelManagerMetricAnomaliesService extends ModelManagerForStoreService<
	MetricAnomalyRecord,
	MetricAnomaly
> {
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	billingFeature = BillingFeature.SMART_ALERTS;

	@cached
	get metricAnomalies() {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	@tracked topItemDefaultSettings!: MetricAnomalies.Settings;

	private getSettingsForTracking(settings: MetricAnomalies.Settings) {
		const granulations = Object.keys(settings.granulations).filter(
			(key) => !!settings.granulations[key as keyof typeof settings.granulations],
		);

		const isEmailDeliveryEnabled = settings.notifications.email;

		const isSlackDeliveryEnabled =
			settings && Object.keys(settings.notifications.slack).length > 0;

		const enabledDeliveries = [];

		if (isEmailDeliveryEnabled) {
			enabledDeliveries.push('email');
		}

		if (isSlackDeliveryEnabled) {
			enabledDeliveries.push('slack');
		}

		return {
			granulation: granulations,
			delivery: enabledDeliveries,
			anomaly_score: settings.anomalyScore,
		};
	}

	createModelForMetricSetup(metricSetup: MetricSetup) {
		const anomaly = this.createNewModel({
			metricSetupKey: metricSetup.key,
			metricSetupSpaceAccessId: metricSetup.spaceAccessId,
			metricSetupDimensions: metricSetup.dimensions,
			metricSetupFilters: metricSetup.filtersData,
		});

		return anomaly;
	}

	edit(model: MetricAnomaly, origin: string, isFromCreateChoicePopupFlow = false) {
		this.mmNotificationsService.openEditPopup(model, origin, isFromCreateChoicePopupFlow);
	}

	createAndEdit(origin: string) {
		const model = this.createNewModel();
		this.edit(model, origin);
	}

	createAndEditForMetricSetup(metricSetup: MetricSetup, origin: string) {
		const model = this.createModelForMetricSetup(metricSetup);
		this.edit(model, origin);
	}

	getIsMetricAnomalyAlertEnabledForMetricSetup(metricSetup: MetricSetup) {
		return !!this.getMetricAnomalyAlertForMetricSetup(metricSetup);
	}

	getMetricAnomalyAlertForMetricSetup(metricSetup: MetricSetup) {
		return this.metricAnomalies.find((metricAnomaly) => {
			return metricAnomaly.metricSetup.isMatchingMetricSetup(metricSetup);
		});
	}

	getMetricAnomalies(preStoreFn?: () => Promise<void>): Promise<void> {
		return this.requestOnce(
			'metricAnomalies',
			() => {
				return this.requestService
					.api<MetricAnomalies.ListResponse>('GET', '/metric-anomalies')
					.then((response) => {
						this.topItemDefaultSettings = response.topItemDefaultSettings;

						return response;
					});
			},
			preStoreFn,
		);
	}

	updateMetricAnomaliesSettings(
		settings: MetricAnomalies.Settings,
		metricAnomaly: MetricAnomaly,
	): Promise<void> {
		return this.requestService
			.api<MetricAnomalies.Settings.UpdateResponse>(
				'PUT',
				`/metric-anomalies/${metricAnomaly.id}/settings`,
				settings,
			)
			.then((response) => {
				this.saveLocal(response.metricAnomalies);
				this.topItemDefaultSettings = response.topItemDefaultSettings;

				this.analyticsService.event(
					TrackingEvent.SMART_ALERTS_SETTINGS_CHANGED,
					this.getSettingsForTracking(settings),
				);
				this.infolayerService.ok('Smart alert settings updated!');
			});
	}

	disableMetricAnomalyAlert(
		metricAnomalyId: MetricAnomaly['id'],
		dataSource: string | null,
		metricTitle: string,
	) {
		const metricAnomaly = this.findModel(metricAnomalyId!);

		if (metricAnomaly) {
			void metricAnomaly.delete().then(() => {
				this.infolayerService.ok('Smart alerts disabled!');

				this.analyticsService.event(TrackingEvent.SMART_ALERTS_DISABLED, {
					type: 'metric',
					origin: 'metrics_detail',
					dataSource,
					metrics: metricTitle,
					...this.getSettingsForTracking(metricAnomaly.settings),
				});
			});
		}
	}

	sendTrackingEventForEnabledAnomaly(anomaly: MetricAnomaly) {
		const dataSource = anomaly.metricSetup.metric?.dataSourceTypeKey || null;
		const metrics = anomaly.metricSetup.selectedMetricTitle;
		this.analyticsService.event(TrackingEvent.SMART_ALERTS_ENABLED, {
			type: 'metric',
			origin: 'metrics_detail',
			dataSource,
			metrics,
			...this.getSettingsForTracking(anomaly.settings),
		});
	}

	sendTrackingEventForDeleteAnomalyAnnotation(
		metricName: string,
		settings?: MetricAnomalies.Settings,
	) {
		this.analyticsService.event(TrackingEvent.SMART_ALERTS_ANOMALY_DELETED, {
			origin: 'metrics_detail',
			metrics: metricName,
			...(settings ? this.getSettingsForTracking(settings) : {}),
		});
	}

	requestOnce(
		variablePrefix: RequestVariablePrefix,
		requestPromiseFn: () => Promise<object | void>,
		preStoreFn?: () => Promise<void>,
	): Promise<void> {
		return this.requestService.dataRequestOnce<RequestVariablePrefix>(
			this,
			variablePrefix,
			requestPromiseFn,
			preStoreFn,
		);
	}

	private saveLocal(metricAnomalies: RecordData.MetricAnomaly.Raw[]) {
		this.storeService.pushPayload({ metricAnomalies });
	}

	// ------ For-store ------

	modelName = ModelName.METRIC_ANOMALY;

	createModel(model: MetricAnomalyRecord): MetricAnomaly {
		return new MetricAnomaly({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/metric-anomalies': ModelManagerMetricAnomaliesService;
	}
}
